/**
 * CountryContext — global selection of "country" used to drive
 * billing (which plans/packs are available), currency suggestions
 * and any region-aware copy.
 *
 * Source of truth precedence (highest to lowest):
 *   1. User profile (`user.country_code`) when authenticated — this is
 *      what the backend uses for billing, so the UI MUST reflect it.
 *   2. localStorage `lumfi_country` — pre-auth defaults.
 *   3. Hard-coded default: "CO" (Colombia).
 *
 * When the user explicitly changes the country via a switcher we PATCH
 * the backend profile so the next time billing is computed it uses the
 * same country the user is seeing (no more silent drift between the
 * sidebar visual and the cobro real).
 */
import { createContext, useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { billingService } from "../services/billingService";
import { profileService } from "../services/profileService";
import { useAuth } from "./AuthContext";

const CountryCtx = createContext(null);

const STORAGE_KEY = "lumfi_country";
const DEFAULT_COUNTRY = "CO";

const COUNTRY_TO_CURRENCY = {
  AR: "ARS", BR: "BRL", CL: "CLP", CO: "COP",
  MX: "MXN", PE: "PEN", UY: "UYU",
};

export function CountryProvider({ children }) {
  const { user } = useAuth();
  const [country, setCountryState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || DEFAULT_COUNTRY,
  );

  // Fetch the enabled countries list. We refetch on focus so a fresh
  // admin change (enabling/disabling a country) propagates to open
  // tabs without a hard reload.
  const { data } = useQuery({
    queryKey: ["billing-countries"],
    queryFn: () => billingService.countries(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const countries = data?.countries || [];

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, country);
  }, [country]);

  // Hydrate from user profile when available — but only if the user's
  // saved country is in the currently-enabled list. If the admin
  // disabled their country between sessions we fall back to the
  // localStorage value (or default) so the UI never points at a
  // disabled region.
  useEffect(() => {
    if (!user?.country_code) return;
    const enabled = new Set(countries.map((c) => c.code));
    if (enabled.size > 0 && !enabled.has(user.country_code)) return;
    setCountryState((prev) => (prev === user.country_code ? prev : user.country_code));
  }, [user?.country_code, countries]);

  // Public setter — when the user explicitly picks a country we ALSO
  // persist it on the backend so /billing/me, /composition/start,
  // /secondaries-for-primary and every other backend touch-point use
  // the same country the user just chose. This kills the drift bug
  // where the sidebar showed CO but the backend silently cobraba MX.
  const setCountry = async (nextCountry) => {
    setCountryState(nextCountry);
    if (!user?.id) return;
    if (nextCountry === user.country_code) return;
    try {
      await profileService.update({
        country: nextCountry,
        currency: COUNTRY_TO_CURRENCY[nextCountry] || undefined,
      });
    } catch (e) {
      // Silent — the optimistic UI already updated.
      // Next /auth/me refresh will reconcile.
      console.warn("Failed to persist country to profile", e);
    }
  };

  const value = {
    country,
    setCountry,
    countries,
    isEnabled: (code) =>
      countries.length === 0 || countries.some((c) => c.code === code),
  };
  return <CountryCtx.Provider value={value}>{children}</CountryCtx.Provider>;
}

export function useCountry() {
  const ctx = useContext(CountryCtx);
  if (!ctx) throw new Error("useCountry must be used within CountryProvider");
  return ctx;
}
